<template>
    <hts-drawer
        v-model="visible"
        :submit="!disabled ? submit : undefined"
        :title="$t('labels.workflowPosition')"
        size="large"
        @close="close">
        <hts-card
            :title="$t('labels.generalInformation')"
            :style="{ minWidth: minWidthScroll }">
            <hts-form-input
                v-model="data.index"
                :label="$t('labels.position')"
                readonly
                :required="!disabled"
                type="number" />

            <hts-form-input
                v-show="disabled"
                v-model="data.nodeTypeName"
                :label="$t('labels.nodeType')"
                readonly />

            <hts-admincp-select-node-type
                v-show="!disabled"
                v-model="data.nodeType"
                v-model:text="data.nodeTypeName"
                :required="!disabled"
                key-id="_id"
                key-label="name"
                :filterable="true"
                :clearable="true"
                confirm-change
                :readonly="disabled"
                :title-confirm="$t('labels.changeNodeType')"
                :detail-confirm="$t('labels.changeNodeTypeInfo')"
                @change="clearBusinessAndTask" />

            <div class="mt-5 mx-1">
                <p>
                    {{ $t('labels.businessAndTask') }} <span
                        v-if="!disabled"
                        class="text-error-500">
                        *
                    </span>
                </p>
            </div>
            <div
                v-for="(business, index) in businessData"
                :key="business"
                class="pb-1">
                <hts-form-disclosure
                    :ref="
                        (el) => {
                            disclosureRefs[index] = el
                        }
                    "
                    :key="index"
                    color="main"
                    size="large"
                    :is-have-children="true">
                    <template #reference>
                        <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white bg-main-500 hover:bg-main-600 w-full">
                            <input
                                type="checkbox"
                                class="w-[17.5px] h-[17.5px]"
                                :checked="Object.keys(data.businesses).includes(business._id)"
                                :value="business._id"
                                :disabled="disabled"
                                @click="(e) => businessClick(e, business._id, business, index)" >
                            <span>
                                {{ business.name }}
                            </span>
                        </div>
                    </template>
                    <template #children>
                        <div
                            v-for="(task, subIndex) in business.tasks"
                            :key="task">
                            <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white mt-3 ml-10 p-2 pl-3 bg-main-400 hover:bg-main-600">
                                <input
                                    class="w-[17.5px] h-[17.5px]"
                                    type="checkbox"
                                    :disabled="disabled"
                                    :checked="Object.keys(data.businesses).includes(business._id) && data.businesses[business._id]?.includes(task._id)"
                                    :value="task._id"
                                    @click="(e) => taskClick(e, business._id, task._id)" >
                                <span>
                                    {{ task.name }}
                                </span>
                            </div>
                        </div>
                    </template>
                </hts-form-disclosure>
            </div>

            <div class="mt-5 mx-1 flex justify-start">
                <div>
                    {{ $t('labels.settingAndOrganizingTasks') }} <span
                        v-if="!disabled"
                        class="text-error-500">
                        *
                    </span>
                </div>
                <hts-tooltip placement="left">
                    <div>
                        <span class="font-bold">
                            {{ $t('labels.dragTaskToSortingTask') }}
                        </span>
                    </div>
                    <template #reference>
                        <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                    </template>
                </hts-tooltip>
            </div>

            <div
                :class="{ 'mt-2': businessData.length }"
                class="flex items-center">
                <div class="w-12 font-bold" />
                <div class="px-3 py-1 box-border rounded-xl w-full flex items-center">
                    <div class="w-7" />
                    <div class="w-full ml-2">
                        <p class="font-bold">
                            {{ $t('labels.task') }}
                        </p>
                    </div>

                    <div class="flex justify-end gap-5 w-full font-bold">
                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.performedBy') }}
                        </div>

                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.activateVehicleStatus') }}
                        </div>

                        <div
                            v-if="!disabled"
                            class="flex items-center justify-start w-10"
                            style="pointer-events: c !important" />
                    </div>
                </div>
            </div>

            <hts-draggable
                v-if="data.tasks && data.tasks.length"
                v-model="data.tasks"
                :style="{ 'pointer-events': disabled ? 'none' : 'auto' }"
                :options="dragOptions">
                <div
                    v-for="(task, index) in data.tasks"
                    :key="task.key"
                    class="task-item"
                    :class="{ 'cursor-pointer': !disabled }">
                    <div
                        :class="{ 'mt-2': index !== 0 }"
                        class="flex items-center">
                        <div class="w-10 font-bold">
                            {{ index + 1 }}
                        </div>
                        <div class="drag-item bg-default-100 dark:bg-default-600 px-3 py-1 box-border rounded-xl w-full flex items-center">
                            <div class="w-7">
                                <hi-bars-3-icon
                                    class="h-6 w-6"
                                    aria-hidden="true" />
                            </div>
                            <div class="w-full ml-2">
                                <p class="font-bold">
                                    {{ task.taskName }}
                                </p>
                                <p class="text-sm opacity-75 italic">
                                    {{ task.businessName }}
                                </p>
                            </div>

                            <div class="flex justify-end gap-5 w-full">
                                <div class="h-full flex items-center w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionResponsibleForImplementation"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        :multiple-level="true"
                                        @click="(action) => handleResponsibleForImplementation(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip class="mt-1">
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.responsibleForImplementation') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="!['HEADWAY_DRIVER', 'HEADWAY', 'CUSTOMER'].includes(task.responsibleForImplementation)"
                                                        size="small"
                                                        icon-name="hi-users-icon" />
                                                    <hts-form-button
                                                        v-else
                                                        size="small">
                                                        <span>
                                                            {{ labelResponsibleForImplementation(task.responsibleForImplementation) }}
                                                        </span>
                                                        <div
                                                            v-if="!disabled"
                                                            class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0">
                                                            <hi-x-circle-icon
                                                                class="h-5 w-5"
                                                                aria-hidden="true"
                                                                @click="task.responsibleForImplementation = ''" />
                                                        </div>
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div class="h-full flex items-center w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionActivateVehicleStatus"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        @click="(action) => handleActivateVehicleStatus(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip class="mt-1">
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.activateVehicleStatus') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="!['HAVE_GOODS', 'HAVE_CONTAINER', 'EMPTY_VEHICLE'].includes(task.activateVehicleStatus)"
                                                        size="small"
                                                        icon-name="hi-truck-icon" />
                                                    <hts-form-button
                                                        v-else
                                                        size="small"
                                                        text-transform="lowercase">
                                                        <span>
                                                            {{ activateVehicleStatusLabel(task.activateVehicleStatus) }}
                                                        </span>
                                                        <div
                                                            v-if="!disabled"
                                                            class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0">
                                                            <hi-x-circle-icon
                                                                class="h-5 w-5"
                                                                aria-hidden="true"
                                                                @click="task.activateVehicleStatus = ''" />
                                                        </div>
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div
                                    v-if="task.value"
                                    class="flex items-center justify-start w-52"
                                    style="pointer-events: auto !important">
                                    <hts-tooltip class="mt-1">
                                        <div>
                                            <span class="font-bold">
                                                {{ $t('labels.completionTime') }}
                                            </span>
                                        </div>
                                        <template #reference>
                                            <hts-form-button size="small">
                                                {{ task.value }} {{ $t('labels.hour') }}
                                            </hts-form-button>
                                        </template>
                                    </hts-tooltip>
                                </div>

                                <hts-tooltip
                                    v-if="!disabled"
                                    class="w-10">
                                    <div>
                                        <span class="font-bold">
                                            {{ $t('labels.delete') }}
                                        </span>
                                    </div>
                                    <template #reference>
                                        <!--                    <hts-form-button -->
                                        <!--                      icon-name="hi-trash-icon" -->
                                        <!--                      @click="deleteTask(task.business, task.task)" -->
                                        <!--                    /> -->
                                        <hi-trash-icon
                                            class="w-6 h-6"
                                            @click="deleteTask(task.business, task.task)" />
                                    </template>
                                </hts-tooltip>
                            </div>
                        </div>
                    </div>
                    <p
                        v-if="index === 0 && errorActiveVehicleStatusTask"
                        class="text-error-500 text-right text-xs mt-1 mr-2">
                        {{ $t('labels.errorActiveVehicleStatusTask') }}
                    </p>
                    <p
                        v-if="!task.responsibleForImplementation && errorResponsibleForImplementation && !disabled"
                        class="text-error-500 text-right text-xs mt-1 mr-2">
                        {{ $t('labels.responsibleForImplementationErrorMessage') }}
                    </p>
                </div>
            </hts-draggable>
        </hts-card>
        <div class="h-32" />
        <hts-modal
            :show="showPopupDeletedTask"
            :title="$t('labels.deleteTaskOrBusiness')"
            type="confirm"
            :content="$t('labels.deleteTaskOrBusinessPositionWarning')"
            @close="showPopupDeletedTask = false"
            @confirm="actionDeletedTask()" />
    </hts-drawer>
</template>
<script lang="ts" setup>
import { ITaskSetting, IWorkflowPosition } from '../-imports/form'

import screen from '~/components/utils/screen'
const { $disableReactive } = useNuxtApp()
const emits = defineEmits(['update'])
const store = $store()
const transportation = ref('')

const disclosureRefs = ref([] as any)
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const data = ref<IWorkflowPosition>({
  index: 0,
  nodeType: '',
  nodeTypeName: '',
  businesses: {} as { [key: string]: string[] },
  tasks: []
})

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

const visible = ref(false)
const businessData = ref<{ [key: string]: any }[]>([])
const actionDeletedTask = ref<Function>(() => {})
const showPopupDeletedTask = ref(false)
const disabled = toRefs(props).disabled
const errorResponsibleForImplementation = ref(false)
const errorActiveVehicleStatusTask = ref(false)
const checkActiveVehicleStatus = ref(false)
const checkHaveTaskLatest = ref(false)

const open = async (positionData: IWorkflowPosition, transportationData: string, activeVehicleStatus = false, haveTaskLatest = false) => {
  data.value = { ...positionData }
  transportation.value = transportationData
  await getBusinessData(data.value.nodeType)

  checkActiveVehicleStatus.value = activeVehicleStatus
  checkHaveTaskLatest.value = haveTaskLatest
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusTask.value = false
  visible.value = true
}

const taskDetailById = (id: string) => {
  const t = store.data.getTasks || []
  const tt = t.find((item: any) => item._id === id)
  return tt
}

const dragOptions = {
  animation: 200
}

const resetFields = () => {
  data.value = {
    index: 0,
    nodeType: '',
    nodeTypeName: '',
    businesses: {},
    tasks: []
  }
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusTask.value = false
  checkHaveTaskLatest.value = false
  businessData.value = [] as any
}

const close = () => {
  resetFields()
  visible.value = false
}

const submit = () => {
  if (!Object.keys(data.value?.businesses).length) {
    process.client && $toast().error($t('labels.pleaseSelectBusiness'))
    return
  }
  if (!data.value?.tasks?.length) {
    process.client && $toast().error($t('labels.pleaseSelectTask'))
    return
  }

  // const length = data.value.tasks.length
  // if (!data.value.tasks[0].activateVehicleStatus) {
  //
  //   return
  // }
  //
  // if (!data.value.tasks[length - 1].activateVehicleStatus) {
  //   return
  // }

  let haveActiveVehicleStatus = false
  for (const task of data.value.tasks) {
    if (!task?.responsibleForImplementation) {
      errorResponsibleForImplementation.value = true
      return
    }

    if (task.activateVehicleStatus) {
      haveActiveVehicleStatus = true
    }
  }

  if (!haveActiveVehicleStatus && checkActiveVehicleStatus.value) {
    errorActiveVehicleStatusTask.value = true
    return
  }

  if (checkHaveTaskLatest.value) {
    // const timeConditions: any = data.value.tasks?.map((item: any) => item?.timeConditions || []).flat() || []

    const timeConditions: any[] = (data.value.tasks || []).flatMap((item: any) => {
      let tc = item?.timeConditions

      if (!tc) {
        const taskDetails = taskDetailById(item.task) || { timeConditions: null }

        const timeConditionsTmp = taskDetails.timeConditions

        if (timeConditionsTmp) {
          tc = timeConditionsTmp
        }
      }

      return tc || []
    })

    const timeType = timeConditions.map((item: any) => item?.timeType || '')

    if (!timeType.includes('LATEST')) {
      process.client && $toast().error('Vị trí cuối cùng của workflow phải có thời hạn muộn nhất.')
      return
    }
  }

  emits('update', $disableReactive(data.value) as IWorkflowPosition)
  close()
}

watch(
  () => data.value.nodeType,
  async (val) => {
    await getBusinessData(val)
  }
)

watch(
  () => data.value.tasks,
  () => {
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusTask.value = false
  },
  { deep: true }
)

const activateVehicleStatusLabel = (type: string) => {
  if (type === 'HAVE_GOODS') {
    return $t('labels.haveGoods')
  }
  if (type === 'HAVE_CONTAINER') {
    return $t('labels.haveContainer')
  }
  if (type === 'EMPTY_VEHICLE') {
    return $t('labels.emptyVehicle')
  }
}
const getBusinessData = async (nodeTypeId: string) => {
  // lấy thông tin nghiệp vụ
  const api = $urlAdmin.business.getByNodeTypeWithTranporation
  if (!nodeTypeId) {
    return
  }

  const data: any = await $api(api, { uriParams: { ID: nodeTypeId, TRANSPORATION: transportation.value } })
  const resData = data?.data?.value
  if (resData?.success) {
    // data update UI
    businessData.value = resData.data
    // data save
    const businesses: { [key: string]: string[] } = resData.data.reduce((acc: any, business: any) => {
      const taskIds = business.tasks.map((task: any) => task._id)
      acc[business._id] = taskIds
      return acc
    }, {})
    data.businesses = businesses
  }
}

const businessClick = (e: any, id: string, business: any, index: number) => {
  e.stopPropagation()
  if (disabled.value) {
    return
  }

  if (!Object.keys(data.value.businesses).includes(id)) {
    const taskIds = business.tasks.map((task: any) => task._id)
    data.value.businesses[id] = taskIds
    console.log('taskIds', taskIds)
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId)
    })
    if (disclosureRefs.value[index] && typeof disclosureRefs.value[index]?.expand === 'function') {
      disclosureRefs.value[index].expand()
    }
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusTask.value = false
  } else {
    if (!showPopupDeletedTask.value) {
      showPopupDeletedTask.value = true
      actionDeletedTask.value = () => businessClick(e, id, business, index)
      e.preventDefault()
      return
    }

    const taskIds = data.value.businesses[id]
    const newObj = Object.keys(data.value.businesses).reduce((acc: any, key: any) => {
      if (key !== id) {
        acc[key] = data.value.businesses[key]
      }
      return acc
    }, {})
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId, 'delete')
    })

    data.value.businesses = newObj

    showPopupDeletedTask.value = false
    actionDeletedTask.value = () => {}
  }
}

const taskClick = (e: any, businessId: string, taskId: string) => {
  if (disabled.value) {
    return
  }

  console.log('taskId', taskId)
  if (Object.keys(data.value.businesses).includes(businessId)) {
    if (!data.value.businesses[businessId].includes(taskId)) {
      data.value.businesses[businessId].push(taskId)
      addTaskData(businessId, taskId)
      errorResponsibleForImplementation.value = false
      errorActiveVehicleStatusTask.value = false
    } else {
      if (!showPopupDeletedTask.value) {
        if (e) {
          e.preventDefault()
        }
        showPopupDeletedTask.value = true
        actionDeletedTask.value = () => taskClick(e, businessId, taskId)
        return
      }

      data.value.businesses[businessId] = data.value.businesses[businessId].filter((item: any) => item !== taskId)
      addTaskData(businessId, taskId, 'delete')

      // bỏ chọn hết tác vụ => bỏ chọn nghiệp vụ
      if (!data.value.businesses[businessId]?.length) {
        data.value.businesses = Object.keys(data.value.businesses).reduce((acc: any, key: any) => {
          if (key !== businessId) {
            acc[key] = data.value.businesses[key]
          }
          return acc
        }, {})
      }
    }
  } else {
    addTaskData(businessId, taskId)
    data.value.businesses[businessId] = [taskId]
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusTask.value = false
  }

  actionDeletedTask.value = () => {}
  showPopupDeletedTask.value = false
}

const addTaskData = (businessId: string, taskId: string, action?: string) => {
  if (disabled.value) {
    return
  }

  const businessesData: { [key: string]: any } = store.data.getBusinesses
  const taskDatas: { [key: string]: any } = store.data.getTasks

  if (action === 'delete') {
    data.value.tasks = data.value.tasks.filter((task: any) => task.key !== `${businessId}-${taskId}`)
    return
  }

  const businessName = businessesData.filter((business: any) => businessId === business._id && !business.deleted)?.[0]?.name || ''

  const taskData: any = taskDatas.filter((task: any) => taskId === task._id && !task.deleted)?.[0] || {}
  const taskName = taskData.name

  if (!businessName || !taskName) {
    return
  }

  data.value.tasks.push({
    key: `${businessId}-${taskId}`,
    business: businessId,
    businessName,
    task: taskId,
    taskName,
    responsibleForImplementation: '',
    activateVehicleStatus: '',
    timeConditions: taskData.timeConditions || []
  })
}

const deleteTask = (businessId: string, taskId: string) => {
  if (disabled.value) {
    return
  }

  actionDeletedTask.value = () => {
    return taskClick('', businessId, taskId)
  }
  showPopupDeletedTask.value = true
}

const handleActivateVehicleStatus = (key: string, actionData: any) => {
  if (disabled.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.activateVehicleStatus = actionData.value
    }
  })
}

const handleResponsibleForImplementation = (key: string, actionData: any) => {
  if (disabled.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.responsibleForImplementation = actionData.value
    }
  })
}

const clearBusinessAndTask = () => {
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusTask.value = false
  data.value.tasks = []
  data.value.businesses = {}
}

const optionActivateVehicleStatus = computed(() => {
  if (transportation.value === 'CONTAINER') {
    return [
      {
        label: $t('labels.haveContainer'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_CONTAINER'
      },
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      },
      {
        label: $t('labels.emptyVehicle'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'EMPTY_VEHICLE'
      }
    ]
  }

  if (transportation.value === 'NON_CONTAINER') {
    return [
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      },
      {
        label: $t('labels.emptyVehicle'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'EMPTY_VEHICLE'
      }
    ]
  }

  return []
})

const optionResponsibleForImplementation = computed(() => {
  return [
    {
      label: store.system.workspace.name,
      class: 'text-blue-600',
      tooltip: $t('labels.taskIsPerformedByDriverOrNot'),
      children: [
        {
          label: $t('labels.driver'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY_DRIVER'
        },
        {
          label: $t('labels.other'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY'
        }
      ]
    },
    {
      label: $t('labels.customer'),
      class: 'text-blue-600 hover:text-blue-500',
      value: 'CUSTOMER'
    }
  ]
})

const labelResponsibleForImplementation = (type: string) => {
  if (type === 'HEADWAY') {
    return $t('labels.other')
  }
  if (type === 'HEADWAY_DRIVER') {
    return $t('labels.driver')
  }
  if (type === 'CUSTOMER') {
    return $t('labels.customer')
  }
}

defineExpose({
  open
})
</script>
